import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Elements, PaymentElement, ExpressCheckoutElement,
         useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LocationPicker from "@/components/LocationPicker";
import TermsAcceptance from "@/components/TermsAcceptance";

let _stripePromise = null;
const getStripe = (pk) => {
  if (!pk) return null;
  if (!_stripePromise) _stripePromise = loadStripe(pk);
  return _stripePromise;
};

function PayInner({ providerIntentId, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const finishOnServer = async () => {
    // Server records the terms acceptance atomically at confirm time (or
    // pulls forward the record already stamped at /payments/intent). The
    // /payments/intent path also requires accepted_terms=true so this is
    // defense-in-depth: no user reaches this call without prior acceptance.
    const r = await api.post("/payments/confirm", {
      provider_intent_id: providerIntentId,
      accepted_terms: true,
    });
    if (r.data.state === "succeeded") { onDone(r.data.message); return true; }
    if (r.data.state === "failed") { setErr("Payment failed. You have not been charged."); return false; }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const { error } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (error) {
      setErr(error.message || "Payment failed. You have not been charged.");
      setBusy(false); return;
    }
    // Never trust the browser callback alone — the server confirms with Stripe.
    await finishOnServer();
    setBusy(false);
  };

  const onExpressConfirm = async () => {
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const { error } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (error) { setErr(error.message || "Payment failed"); setBusy(false); return; }
    await finishOnServer();
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5 mt-6" data-testid="voice-payment-form">
      <ExpressCheckoutElement onConfirm={onExpressConfirm}
                              options={{ paymentMethods: { applePay: "auto", googlePay: "auto" } }} />
      <div className="oh-label text-[color:var(--oh-ink-3)] text-center">or pay with card</div>
      <PaymentElement options={{ layout: "tabs" }} />
      {err && <div className="text-sm text-[color:var(--oh-red)]" data-testid="voice-payment-error">{err}</div>}
      <button type="submit" disabled={!stripe || busy}
              className="btn-red w-full" data-testid="voice-pay">
        {busy ? "Processing…" : "Pay €1 · Add my voice"}
      </button>
    </form>
  );
}

export default function VoiceModal({ open, onOpenChange, onConfirm, onPassItOn, messageTitle, messageId, referralRef, isFounding = false }) {
  const [step, setStep] = useState("location"); // location | init | pay | processing | success | free
  const [config, setConfig] = useState(null);
  const [intent, setIntent] = useState(null);
  const [publishedMsg, setPublishedMsg] = useState(null);
  // Contractual acceptance of the Terms of Service. Required — checkbox is
  // rendered unchecked on every open, and every server call that could
  // create a payment record refuses to proceed without it.
  const [accepted, setAccepted] = useState(false);

  // Confirmed city+country from LocationPicker. lat/lon are the geocoder's
  // CITY-CENTROID — never device GPS, never IP-derived. See TASK 3 spec.
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState(null);
  const [countryText, setCountryText] = useState("");
  const [cityText, setCityText] = useState("");

  // CF-IPCountry sanity check (see /api/geocode/check-country). The
  // backend only returns "match" / "mismatch" / "unknown" — we never
  // learn (or persist) the CF-IPCountry value itself. `mismatchAck`
  // exists only in local component state for the current VOICE flow;
  // nothing is written to storage or the server. Non-blocking: user
  // may confirm the selected country and continue, or change it.
  const [mismatchAck, setMismatchAck] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);
  const [checkBusy, setCheckBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("location"); setIntent(null); setPublishedMsg(null);
    setCountry(null); setCity(null); setCountryText(""); setCityText("");
    setAccepted(false);
    setMismatchAck(false); setShowMismatch(false); setCheckBusy(false);
    // Preload Stripe config so the moment the user confirms location we
    // can show the checkout without a second spinner.
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [open]);

  const locationPayload = () => {
    // City is the source of truth when available; otherwise fall back to
    // the confirmed country's centroid. Referral survives untouched.
    const src = city || country || {};
    return {
      ref: referralRef || null,
      country: (country?.country || src.country) || null,
      country_code: (country?.country_code || src.country_code || "").toUpperCase() || null,
      region: src.region || null,
      city: city?.city || null,   // NEVER auto-fill city from country
      lat: (typeof src.lat === "number" ? src.lat : null),
      lon: (typeof src.lon === "number" ? src.lon : null),
      display_name: src.display_name || null,
    };
  };

  const confirmLocation = async () => {
    if (!country) { toast.error("Please choose a country from the list."); return; }
    if (!city) { toast.error("Please choose your city from the list."); return; }
    if (!accepted) { toast.error("Please accept the Terms of Service to continue."); return; }

    // CF-IPCountry sanity check — fail-open. We only gate the flow on
    // an explicit "mismatch" response, and even then only once (user
    // acks with a click). On "unknown", "match", or any network error
    // we proceed silently. Nothing about this check is persisted.
    if (!mismatchAck) {
      setCheckBusy(true);
      try {
        const cc = (country.country_code || "").toUpperCase();
        if (cc && /^[A-Z]{2}$/.test(cc)) {
          const chk = await api.get(`/geocode/check-country?country_code=${cc}`);
          if (chk?.data?.result === "mismatch") {
            setShowMismatch(true);
            setCheckBusy(false);
            return;
          }
        }
      } catch (_) {
        // fail-open: any error → treat as unknown, continue silently.
      }
      setCheckBusy(false);
    }

    setStep("init");
    try {
      const body = { ...locationPayload(), accepted_terms: true };
      const r = await api.post(`/payments/intent?msg_id=${messageId}`, body);
      if (r.data.state === "not_required") {
        // Founding MESSAGE — free voice, no Stripe. Location travels with it.
        const free = await api.post(`/payments/free-voice?msg_id=${messageId}`, body);
        setPublishedMsg(free.data.message); setStep("free");
        onConfirm && onConfirm();
      } else if (r.data.state === "succeeded") {
        setStep("success"); onConfirm && onConfirm();
      } else {
        setIntent(r.data); setStep("pay");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      setStep("location");
    }
  };

  const options = useMemo(() => intent?.client_secret
    ? { clientSecret: intent.client_secret,
        appearance: { theme: "stripe", variables: { colorPrimary: "#B4322A", fontFamily: "Manrope, sans-serif" } } }
    : null, [intent]);

  const onPaymentDone = (msg) => {
    setPublishedMsg(msg); setStep("success");
    toast.success("Your voice is in.");
    onConfirm && onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--oh-bg)] border oh-hairline rounded-none max-w-md p-0 max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain"
                     data-testid="voice-modal">
        <div className="px-8 sm:px-10 py-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
          <DialogTitle className="oh-label" data-testid="voice-modal-title">Add your voice</DialogTitle>
          <h2 className="font-serif-editorial text-3xl sm:text-4xl leading-[1.05] mt-4 text-[color:var(--oh-ink)]">
            {isFounding ? "FREE · One human. One voice." : "€1 · One human. One voice."}
          </h2>
          <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
            {isFounding
              ? "Add your VOICE to this MESSAGE for free."
              : "Your €1 adds one verified VOICE to this MESSAGE. It is a participation fee to VOIHU and is not a donation to the MESSAGE or its cause."}
          </p>
          <div className="mt-6 border oh-hairline p-5">
            <div className="oh-label">You are supporting</div>
            <div className="font-serif-editorial text-xl mt-2 leading-tight">{messageTitle}</div>
          </div>

          {step === "location" && (
            <div className="mt-8" data-testid="voice-location">
              <div className="oh-label">Where are you adding your voice from?</div>
              <p className="text-[color:var(--oh-ink-2)] mt-2 text-xs leading-relaxed">
                City-level only. We never store your address, GPS or IP.
              </p>
              <div className="mt-5">
                <label className="oh-label">Country</label>
                <LocationPicker
                  mode="country" value={countryText} selected={country}
                  onChange={(v) => { setCountryText(v); if (!v) setCountry(null); }}
                  onSelect={(sel) => {
                    setCountry(sel);
                    if (sel) setCountryText(sel.country);
                    if (sel && city && (city.country_code || "").toUpperCase() !== sel.country_code) {
                      setCity(null); setCityText("");
                    }
                    // Any country change invalidates a prior mismatch
                    // acknowledgement — the check must re-run against
                    // the new selection on the next Continue click.
                    setMismatchAck(false); setShowMismatch(false);
                  }}
                  placeholder="Search for a country"
                  testid="voice-country" autoFocus />
              </div>
              <div className="mt-5">
                <label className="oh-label">
                  City · required{country ? "" : " (pick a country first)"}
                </label>
                <LocationPicker
                  mode="city" value={cityText} selected={city}
                  countryCode={country?.country_code}
                  onChange={(v) => { setCityText(v); if (!v) setCity(null); }}
                  onSelect={(sel) => {
                    setCity(sel);
                    if (sel) setCityText(sel.city || sel.display_name.split(",")[0].trim());
                  }}
                  placeholder={country ? "Search for a city or locality" : "Search for a city"}
                  testid="voice-city" />
              </div>
              <TermsAcceptance
                accepted={accepted}
                onChange={setAccepted}
                mode={isFounding ? "free" : "paid"}
                testId="voice-terms"
              />
              {showMismatch && !mismatchAck && (
                <div className="mt-6 border oh-hairline p-4"
                     data-testid="voice-country-mismatch">
                  <div className="oh-label text-[color:var(--oh-red)]">
                    Please confirm your location
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--oh-ink-2)]">
                    Your selected country differs from your current connection
                    location. This can happen when travelling or using a VPN.
                    Please confirm that <strong>{country?.country}</strong> is
                    the location you want to use for this VOICE.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setMismatchAck(true);
                        setShowMismatch(false);
                        // Immediately proceed with the flow the user
                        // just confirmed. No state is persisted.
                        confirmLocation();
                      }}
                      className="btn-red flex-1"
                      data-testid="voice-country-mismatch-confirm">
                      Confirm {country?.country_code}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Wipe the current selection and let the user
                        // pick again from scratch — no ack is retained.
                        setCountry(null); setCountryText("");
                        setCity(null); setCityText("");
                        setShowMismatch(false); setMismatchAck(false);
                      }}
                      className="btn-ghost flex-1"
                      data-testid="voice-country-mismatch-change">
                      Change location
                    </button>
                  </div>
                </div>
              )}
              <button onClick={confirmLocation}
                      disabled={!country || !city || !accepted || checkBusy || (showMismatch && !mismatchAck)}
                      className="btn-red w-full mt-8" data-testid="voice-location-confirm">
                {checkBusy ? "Checking…" : "Continue"}
              </button>
            </div>
          )}

          {step === "init" && (
            <div className="mt-10 text-center py-8">
              <div className="oh-label mb-4">Preparing checkout</div>
              <div className="mx-auto w-8 h-8 border-2 border-[color:var(--oh-ink)] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {step === "pay" && options && config?.publishable_key && (
            <Elements stripe={getStripe(config.publishable_key)} options={options}>
              <PayInner providerIntentId={intent.provider_intent_id} onDone={onPaymentDone} />
            </Elements>
          )}

          {step === "free" && (
            <div className="mt-8" data-testid="voice-free-success">
              <div className="oh-label text-[color:var(--oh-red)]">Founding voice — free</div>
              <h3 className="font-serif-editorial text-4xl mt-4 leading-none">Your voice is in.</h3>
              <button
                onClick={() => {
                  // Single click hand-off: close this modal AND open the
                  // existing PASS IT ON sheet in one action. Preserves the
                  // participant's referral context because PassItOn reads
                  // `incomingRef` from Message.jsx, which is unchanged.
                  if (onPassItOn) onPassItOn(); else onOpenChange(false);
                }}
                className="btn-primary w-full mt-8" data-testid="voice-close">
                Pass it on →
              </button>
            </div>
          )}

          {step === "success" && (
            <div className="mt-8">
              <div className="oh-label text-[color:var(--oh-red)]">Payment successful</div>
              <h3 className="font-serif-editorial text-4xl mt-4 leading-none">Your voice is in.</h3>
              <button
                onClick={() => {
                  if (onPassItOn) onPassItOn(); else onOpenChange(false);
                }}
                className="btn-primary w-full mt-8" data-testid="voice-close">
                Pass it on →
              </button>
            </div>
          )}

          <p className="mt-6 text-[10px] uppercase tracking-widest text-[color:var(--oh-ink-3)]"
             data-testid="voice-merchant">
            Secure payment by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
